import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import { FieldRadio, FormControl, Tooltip } from 'components/atoms';
import { FieldsContainer } from 'form/form.styles';
import Input from 'components/atoms/field-input';
import { useFormContext } from 'react-hook-form';
import Select from 'components/atoms/field-select';
import { ReactComponent as InfoIcon } from './../../../../../assets/svg/Icons/Info.svg';
import PerformEntityResolution from 'components/molecules/perform-entity-resolution';
import { GatewayPermissions } from 'auth/constants/permissions';
import EntityResolutionModal from 'components/organisms/entity-resolution-modal/EntityResolutionModal';
import { Organization } from 'components/organisms/entity-resolution-modal/types';
import { useCountryHelper } from 'helpers/countriesHelper';
import { ThirdPartyDataConfigDictionary } from 'services/getThirdPartyConfiguration';
import { SideBySide } from '../../ThirdPartyFormModal.styles';
import { useTerminologyConfig, useUserSettings } from 'context/DataContext';
import { ThirdPartyType } from 'types';

interface StepOneProps {
  setShowAdditionalInfo: Dispatch<SetStateAction<boolean>>;
  thirdPartyDataConfig: ThirdPartyDataConfigDictionary;
  isEdit: boolean;
}

const EssentialInfo: FC<StepOneProps> = ({
  setShowAdditionalInfo,
  isEdit,
  thirdPartyDataConfig,
}) => {
  const {
    register,
    setValue,
    formState: { errors, touchedFields },
    watch,
  } = useFormContext();

  const [isEntityResolutionModalOpen, setEntityResolutionModalOpen] = useState(false);

  const { hasPermission } = useUserSettings();
  const terminologyConfig = useTerminologyConfig();
  const type = watch('type');
  const isBusiness = type === ThirdPartyType.Business;

  const companyName = watch('name');
  const primaryCountry = watch('primaryCountry');

  const hasEntityResolutionPermission = hasPermission(GatewayPermissions.ThirdPartyLookup);

  const isEntityResolutionModalEnabled =
    hasPermission(GatewayPermissions.ThirdPartyLookup) && companyName && !!primaryCountry;

  const populateFields = (organization: Organization) => {
    const dataToPopulate = {
      name: organization?.primaryName ?? '',
      duns: organization?.duns,
      swift: organization?.swift,
      registrationNumber: organization?.registrationNumbers[0]?.registrationNumber,
      addressLineOne: organization.primaryAddress?.streetAddress?.lineOne,
      zipCode: organization.primaryAddress?.postalCode,
      companyUrl: organization?.websiteAddress[0],
    };

    setValue('name', dataToPopulate.name);
    setValue('duns', dataToPopulate.duns);
    setValue('swift', dataToPopulate.swift);
    setValue('registrationNumber', dataToPopulate.registrationNumber);
    setValue('addressLineOne', dataToPopulate.addressLineOne);
    setValue('zipCode', dataToPopulate.zipCode);
    setValue('companyUrl', dataToPopulate.companyUrl);
  };

  const isValid = (field: string) => {
    return !errors?.[field]?.message && !touchedFields?.[field]?.message;
  };
  const { sortedCountries: countries } = useCountryHelper();

  return (
    <FieldsContainer>
      {isEdit && (
        <FormControl errorMessage={errors.type?.message}>
          <SideBySide>
            <FieldRadio
              label="Company"
              id="company"
              value="Business"
              name="type"
              showBorder={false}
              labelRight
              leftAlign
              small
              register={register}
            >
              <span>Company</span>
            </FieldRadio>
            <FieldRadio
              label="Individual"
              id="individual"
              value="Individual"
              name="type"
              showBorder={false}
              labelRight
              leftAlign
              small
              register={register}
            >
              <span>Individual</span>
            </FieldRadio>
          </SideBySide>
        </FormControl>
      )}
      <FormControl errorMessage={errors.name?.message}>
        <Input
          label={`${isBusiness ? 'Company' : 'Individual'} name`}
          id="name"
          name="name"
          register={register}
          fullWidth
          valid={isValid('name')}
          required={thirdPartyDataConfig.name?.isMandatory}
        />
      </FormControl>
      <FormControl errorMessage={errors.alternativeName?.message}>
        <Input
          label={`${isBusiness ? 'Company' : 'Individual'} name (alternative script)`}
          id="alternativeName"
          name="alternativeName"
          register={register}
          fullWidth
          valid={isValid('alternativeName')}
          required={thirdPartyDataConfig.alternativeName?.isMandatory}
        />
      </FormControl>
      <FormControl errorMessage={errors.primaryCountry?.message}>
        <Select
          label="Primary country"
          id="primaryCountry"
          register={register}
          options={countries}
          placeholder="Please select"
          placeholderValue="DEFAULT"
          fullWidth
          valid={isValid('primaryCountry')}
          required={thirdPartyDataConfig.primaryCountry?.isMandatory}
        />
      </FormControl>
      {isBusiness && (
        <>
          <div style={{ margin: '25px 0 30px' }}>
            <PerformEntityResolution
              enabled={isEntityResolutionModalEnabled}
              onClick={() => setEntityResolutionModalOpen(true)}
              tooltipText={
                hasEntityResolutionPermission ? (
                  <>
                    <p>
                      The Company Lookup function can be used to quickly auto-populate a new Third
                      Party profile with additional information you may not immediately have access
                      to.
                    </p>
                    <br />
                    <br />
                    <p>
                      Once the minimum required information of Company Name and Primary Country has
                      been provided, you can select the "Perform Company Lookup" function which will
                      automatically cross-check the input information against a global database of
                      registered companies. If one or more potential matches are identified, they
                      will be presented to you.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      The Company Lookup function is a supplementary service, which when subscribed
                      to, can be used to quickly auto-populate a new Third Party profile with
                      additional information you may not immediately have access to.
                    </p>
                    <br />
                    <br />
                    <p>
                      Once enabled on your Gateway account, after the minimum required information
                      of Company Name and Primary Country has been provided, you can select the
                      "Perform Company Lookup" function which will automatically cross-check the
                      input information against a global database of registered companies. If one or
                      more potential matches are identified, they will be presented to you.
                    </p>
                  </>
                )
              }
            />
          </div>
          {isEntityResolutionModalOpen && (
            <EntityResolutionModal
              isOpen={true}
              onClose={() => setEntityResolutionModalOpen(false)}
              onCancel={() => setEntityResolutionModalOpen(false)}
              onProceed={(organization) => {
                setEntityResolutionModalOpen(false);
                populateFields(organization);
                setShowAdditionalInfo(false);
              }}
              searchPayload={{
                name: companyName,
                countryISOAlpha2Code: primaryCountry,
              }}
            />
          )}
        </>
      )}
      <FormControl errorMessage={errors.internalId?.message}>
        <Input
          label={terminologyConfig?.internalIdSingularNaming}
          id="internalId"
          name="internalId"
          register={register}
          fullWidth
          valid={isValid('internalId')}
          required={thirdPartyDataConfig.internalId?.isMandatory}
        />
      </FormControl>
      {isBusiness && (
        <FormControl errorMessage={errors.duns?.message}>
          <Input
            label="DUNS"
            id="duns"
            name="duns"
            register={register}
            fullWidth
            valid={isValid('duns')}
            required={thirdPartyDataConfig.duns?.isMandatory}
            labelEndIcon={
              <Tooltip
                trigger="click"
                tooltipText={
                  (
                    <>
                      DUNS is a unique nine-digit identification number assigned to businesses to
                      help distinguish one business from another.
                    </>
                  ) as any
                }
                hideArrow
                placement="right-start"
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 10],
                    },
                  },
                ]}
              >
                <span style={{ marginLeft: 5 }}>
                  <InfoIcon className="clickable" />
                </span>
              </Tooltip>
            }
          />
        </FormControl>
      )}
    </FieldsContainer>
  );
};

export default EssentialInfo;
